/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';
import styled from 'astroturf/react';
import { CSSTransition } from 'react-transition-group';
import pluralize from 'pluralize';

import AuthorPopover from '../shared/popovers/author_popover';
import ResearchInterestList from './research_interest_list';
import WorkActions from './WorkActions';
import { Work, Author } from 'js/search/react/canonical_work_results';

const WorkCardWrapper = styled.div`
  margin-bottom: 32px;
`;

const WorkTitle = styled.a`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 21px;
  color: #0849b8;
  text-decoration: none;

  &:hover {
    color: #0558e5;
    text-decoration: underline;
  }

  &:active {
    color: #222222;
    text-decoration: underline;
  }

  &:visited {
    color: #784dae;
  }
`;

const WorkSubLine = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 3px;

  & > * + *::before {
    content: '•';
    margin: 0 10px;
    color: #777;
  }
`;

const WorkMetaData = styled.div<{ italicized?: boolean }>`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;

  color: #444444;

  &.italicized {
    font-style: italic;
  }
`;

const AuthorsList = styled.div`
  display: flex;
  max-width: 300px;
  flex-wrap: wrap;
`;

const AuthorNameWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 0 0 auto;
  color: #333;
  text-decoration: none;
  cursor: pointer;
  max-width: 100%;

  &:not(:last-child):after {
    content: ',';
    margin-right: 4px;
  }
`;

const AuthorNameTitle = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  flex: 0 0 auto;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WorkActionsWrapper = styled.div`
  margin-top: -6px;
`;

type Props = {
  work: Work;
  premium: boolean;
  source: string;
  loadWorkShareSuggestions: any;
  reportWork: any;
  addToLibrary: any;
  removeFromLibrary: any;
  fetchActorData: any;
  followUser: any;
  unfollowUser: any;
  fetchResearchInterestFollowerCount: any;
  followResearchInterest: any;
  unfollowResearchInterest: any;
  addClickTracking?: (url: string) => string;
  showBulkDownloadButton?: boolean;
  showBulkDownloadModal?: boolean;
  loggedIn?: boolean;
};

const SearchWorkCardRedesign = ({
  work,
  premium,
  source,
  loadWorkShareSuggestions,
  reportWork,
  addToLibrary,
  removeFromLibrary,
  fetchActorData,
  followUser,
  unfollowUser,
  fetchResearchInterestFollowerCount,
  followResearchInterest,
  unfollowResearchInterest,
  addClickTracking,
  showBulkDownloadButton = true,
  showBulkDownloadModal = true,
  loggedIn = true,
}: Props) => {
  const addClickTrackingFunction = addClickTracking || ((url: string) => url);

  return (
    <WorkCardWrapper className="work-card-wrapper">
      <WorkTitle href={addClickTrackingFunction(work.internalUrl)}>
        {work.title}
      </WorkTitle>
      <WorkSubLine>
        {!!work.metadata.publicationDate?.year && (
          <WorkMetaData>{work.metadata.publicationDate.year}</WorkMetaData>
        )}

        <AuthorsList>
          {work.authors.map((author) => (
            // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            <AuthorName
              key={author.id}
              author={author}
              fetchActorData={fetchActorData}
              followUser={followUser}
              unfollowUser={unfollowUser}
              addClickTracking={addClickTrackingFunction}
              enablePopover={true}
              loggedIn={loggedIn}
            />
          ))}
        </AuthorsList>

        {!!work.citationCount && (
          <WorkMetaData>
            {pluralize('citation', work.citationCount, true)}
          </WorkMetaData>
        )}

        {/* Q1 2025 SEO effort to improve topics pages. We don't want to link
        to topics pages logged out in case the topic is not indexable. */}
        {work.researchInterests?.length > 0 && loggedIn && (
          <ResearchInterestList
            interests={work.researchInterests}
            fetchResearchInterestFollowerCount={
              fetchResearchInterestFollowerCount
            }
            followResearchInterest={followResearchInterest}
            unfollowResearchInterest={unfollowResearchInterest}
            addClickTracking={addClickTracking}
            loggedIn={loggedIn}
            showSearchRedesign={true}
          />
        )}
        {work.metadata.publicationName && (
          <WorkMetaData italicized={true}>
            {work.metadata.publicationName}
          </WorkMetaData>
        )}
      </WorkSubLine>
      <WorkActionsWrapper>
        <WorkActions
          work={work}
          premium={premium}
          reportWork={reportWork}
          loadWorkShareSuggestions={loadWorkShareSuggestions}
          addToLibrary={addToLibrary}
          removeFromLibrary={removeFromLibrary}
          showBulkDownloadButton={showBulkDownloadButton}
          showBulkDownloadModal={showBulkDownloadModal}
          source={source}
          loggedIn={loggedIn}
          showSearchRedesign={true}
        />
      </WorkActionsWrapper>
    </WorkCardWrapper>
  );
};

const WorkCardLink = styled.a`
  color: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
  &:focus {
    text-decoration: none;
  }
`;

type AuthorNameProps = {
  author: Author; // from work's author list, see WorkCard propTypes for details
  fetchActorData: (author: Author) => void; // redux action to fetch an actor's async data (views, etc)
  followUser: (id: number | string, callback: () => void) => void; // redux action to issue a follow user request
  unfollowUser: (id: number | string, callback: () => void) => void; // redux action to issue an unfollow user request
  addClickTracking: (url: string) => string; // function that wraps urls for explicit click tracking
  enablePopover: boolean;
  loggedIn: boolean;
};

type AuthorNameState = {
  showPopover: boolean;
};

class AuthorName extends Component<AuthorNameProps, AuthorNameState> {
  static defaultProps = {
    loggedIn: true,
  };

  constructor(props: AuthorNameProps) {
    super(props);
    this.state = {
      showPopover: false,
    };
  }

  render() {
    const {
      author,
      addClickTracking,
      enablePopover,
      fetchActorData,
      followUser,
      unfollowUser,
      loggedIn,
    } = this.props;

    // For ownerless works, external authors don't have URLs
    if (!author.url) {
      return (
        <AuthorNameWrapper>
          <AuthorNameTitle>{author.displayName}</AuthorNameTitle>
        </AuthorNameWrapper>
      );
    }

    return (
      <AuthorNameWrapper
        onMouseEnter={enablePopover ? this.showPopover : () => null}
        onMouseLeave={enablePopover ? this.hidePopover : () => null}
      >
        <WorkCardLink href={addClickTracking(author.url)}>
          <AuthorNameTitle>{author.displayName}</AuthorNameTitle>
        </WorkCardLink>
        {enablePopover && (
          <CSSTransition
            classNames="work-card--author-popover-outer-wrapper"
            in={this.state.showPopover}
            timeout={{ enter: 200, exit: 200 }}
            unmountOnExit
          >
            <AuthorPopover
              author={author}
              fetchActorData={fetchActorData}
              followUser={followUser}
              unfollowUser={unfollowUser}
              addClickTracking={addClickTracking}
              loggedIn={loggedIn}
              reduceTopPosition={true}
            />
          </CSSTransition>
        )}
      </AuthorNameWrapper>
    );
  }

  showPopover = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    this.setState({ showPopover: true });
  };

  hidePopover = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    this.setState({ showPopover: false });
  };
}

export default SearchWorkCardRedesign;
